export { manifest } from './manifest.js';
import { manifest } from './manifest.js';

export const MAX_ASSET_BYTES = 8 * 1024 * 1024;
const FORMATS = new Set(['image/png', 'image/webp', 'image/gif']);
const safePath = (value) => typeof value === 'string' && value.length > 0 && !value.startsWith('/') && !value.includes('\\') && !value.split('/').some((part) => !part || part === '.' || part === '..') && !/[?#\u0000-\u001f]/.test(value);

/** Return every actionable manifest failure, without downloading or modifying objects. */
export function validateManifest(value) {
  const errors = [];
  if (!value || value.schemaVersion !== 1) errors.push('schemaVersion: expected 1');
  if (!value || typeof value.snapshotVersion !== 'string' || !value.snapshotVersion.trim()) errors.push('snapshotVersion: required');
  if (value?.origin !== 'https://storage.googleapis.com/mario-kart-graphics/') errors.push('origin: expected approved public artwork bucket');
  if (!Array.isArray(value?.assets) || value.assets.length === 0) return [...errors, 'assets: expected nonempty array'];
  const ids = new Set();
  const paths = new Set();
  for (const [index, asset] of value.assets.entries()) {
    const name = typeof asset?.id === 'string' ? asset.id : `assets[${index}]`;
    if (!asset || typeof asset !== 'object') { errors.push(`${name}: expected object`); continue; }
    if (!safePath(asset.id)) errors.push(`${name}: invalid logical ID`);
    if (ids.has(asset.id)) errors.push(`${name}: duplicate logical ID`);
    ids.add(asset.id);
    for (const field of ['object', 'bundledPath']) if (!safePath(asset[field])) errors.push(`${name}: unsafe ${field}`);
    if (paths.has(asset.bundledPath)) errors.push(`${name}: duplicate bundledPath`);
    paths.add(asset.bundledPath);
    if (asset.fallbackPath !== 'fallbacks/artwork.svg') errors.push(`${name}: unsupported fallbackPath`);
    if (typeof asset.generation !== 'string' || !/^[1-9]\d*$/.test(asset.generation)) errors.push(`${name}: immutable GCS generation required`);
    if (!/^[a-f0-9]{64}$/.test(asset.sha256 ?? '')) errors.push(`${name}: invalid SHA-256 checksum`);
    if (!FORMATS.has(asset.contentType)) errors.push(`${name}: unsupported contentType`);
    const extensions = { 'image/png': '.png', 'image/webp': '.webp', 'image/gif': '.gif' };
    if (typeof asset.object === 'string' && extensions[asset.contentType] && !asset.object.endsWith(extensions[asset.contentType])) errors.push(`${name}: object extension does not match contentType`);
    for (const field of ['width', 'height']) if (!Number.isSafeInteger(asset[field]) || asset[field] < 1 || asset[field] > 16384) errors.push(`${name}: ${field} must be 1..16384`);
    if (!Number.isSafeInteger(asset.bytes) || asset.bytes < 1 || asset.bytes > MAX_ASSET_BYTES) errors.push(`${name}: bytes must be 1..${MAX_ASSET_BYTES} (oversized asset)`);
    if (typeof asset.label !== 'string' || !asset.label.trim()) errors.push(`${name}: accessibility label required`);
  }
  return errors;
}

export function remoteAssetUrl(asset, catalog = manifest) {
  return `${catalog.origin}${asset.object.split('/').map(encodeURIComponent).join('/')}?generation=${asset.generation}`;
}

/** Desktop callers must select bundled or development; neither performs runtime requests. */
export function resolveAsset(id, { mode = 'web', baseUrl = '', catalog = manifest } = {}) {
  const errors = validateManifest(catalog);
  if (errors.length) throw new Error(errors.join('\n'));
  const asset = catalog.assets.find((candidate) => candidate.id === id);
  if (!asset) throw new Error(`Unknown asset ID: ${id}`);
  if (mode === 'web') return remoteAssetUrl(asset, catalog);
  if (mode !== 'bundled' && mode !== 'development') throw new Error(`Unknown asset mode: ${mode}`);
  const path = mode === 'bundled' ? asset.bundledPath : asset.fallbackPath;
  return `${baseUrl.replace(/\/$/, '')}/${path.split('/').map(encodeURIComponent).join('/')}`;
}
